import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Image as ImageIcon, Video as VideoIcon, Trash2, Loader2, Search, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const absUrl = (u) => (u?.startsWith("http") ? u : `${BASE}${u}`);

const formatSize = (bytes) => {
  if (!bytes) return "-";
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

/**
 * Reusable media library dialog.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (bool) => void
 *   token: auth token
 *   mode: "image" | "video" | "all" — filters tabs
 *   onSelect: (mediaItem) => void — called when user picks an item; dialog closes automatically
 *   language: "es" | "en"
 */
export const MediaLibrary = ({ open, onOpenChange, token, mode = "all", onSelect, language = "es" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(mode === "video" ? "video" : "image");
  const [search, setSearch] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const t = (es, en) => (language === "es" ? es : en);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/media`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data.items || []);
    } catch {
      toast.error(t("Error al cargar la galería", "Error loading library"));
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) fetchItems();
  }, [open, fetchItems]);

  useEffect(() => {
    if (mode !== "all") setTab(mode === "video" ? "video" : "image");
  }, [mode]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t("¿Eliminar este archivo de la galería?", "Delete this file from the library?"))) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/media/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success(t("Archivo eliminado", "File deleted"));
    } catch {
      toast.error(t("No se pudo eliminar", "Delete failed"));
    } finally {
      setDeletingId(null);
    }
  };

  const tabs = mode === "all"
    ? [
        { key: "image", label: t("Imágenes", "Images"), icon: ImageIcon },
        { key: "video", label: t("Videos/GIF", "Videos/GIF"), icon: VideoIcon },
      ]
    : [];

  const filtered = items.filter((i) => {
    if (mode === "image") return i.type === "image";
    if (mode === "video") return i.type === "video" || i.type === "gif";
    // all
    if (tab === "image") return i.type === "image";
    return i.type === "video" || i.type === "gif";
  }).filter((i) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (i.original_name || "").toLowerCase().includes(q) || (i.filename || "").toLowerCase().includes(q);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="media-library-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-indigo-600" />
            {t("Galería de Medios", "Media Library")}
          </DialogTitle>
          <DialogDescription>
            {t("Reutiliza imágenes y videos ya subidos sin volver a cargar archivos.",
               "Reuse previously uploaded images and videos without re-uploading.")}
          </DialogDescription>
        </DialogHeader>

        {/* Tabs + search */}
        <div className="flex items-center gap-3 pb-3 border-b">
          {tabs.length > 0 && (
            <div className="flex gap-1">
              {tabs.map(({ key, label, icon: Icon }) => (
                <Button
                  key={key}
                  size="sm"
                  variant={tab === key ? "default" : "outline"}
                  onClick={() => setTab(key)}
                  className={tab === key ? "bg-stone-900" : ""}
                  data-testid={`media-tab-${key}`}
                >
                  <Icon className="w-4 h-4 mr-1" /> {label}
                </Button>
              ))}
            </div>
          )}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("Buscar por nombre...", "Search by name...")}
              className="pl-8 h-9"
              data-testid="media-search"
            />
          </div>
          <div className="ml-auto text-xs text-stone-500">
            {filtered.length} {t("archivo(s)", "file(s)")}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-auto py-4">
          {loading ? (
            <div className="flex items-center justify-center h-40 text-stone-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("Cargando...", "Loading...")}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-stone-400" data-testid="media-empty">
              <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>{t("La galería está vacía. Sube tu primer archivo.",
                     "The library is empty. Upload your first file.")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map((m) => (
                <div
                  key={m.id}
                  className="group relative rounded-lg border border-stone-200 bg-stone-50 overflow-hidden cursor-pointer hover:ring-2 hover:ring-indigo-400 transition-all"
                  onClick={() => { onSelect?.(m); onOpenChange?.(false); }}
                  data-testid={`media-item-${m.id}`}
                >
                  <div className="aspect-video bg-stone-100 flex items-center justify-center">
                    {m.type === "image" ? (
                      <img src={absUrl(m.url)} alt={m.original_name} className="w-full h-full object-cover" loading="lazy" />
                    ) : m.type === "gif" ? (
                      <img src={absUrl(m.url)} alt={m.original_name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <video src={absUrl(m.url)} className="w-full h-full object-cover" muted preload="metadata" />
                    )}
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-medium text-stone-800 truncate" title={m.original_name}>
                      {m.original_name}
                    </p>
                    <p className="text-[10px] text-stone-500">{formatSize(m.size)}</p>
                  </div>
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <Button size="sm" className="bg-white text-stone-900 hover:bg-stone-100"
                      data-testid={`media-use-${m.id}`}>
                      <Check className="w-4 h-4 mr-1" /> {t("Usar", "Use")}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-8 w-8 p-0"
                      onClick={(e) => handleDelete(m.id, e)}
                      disabled={deletingId === m.id}
                      data-testid={`media-delete-${m.id}`}
                    >
                      {deletingId === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaLibrary;
